import { Location } from 'history';
import { rem } from 'polished';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { FatalAttraction as FA } from '@vpr/tracking';

import { useLazyQuery, useQuery } from '@vimeo/core/hooks/use-api-resource';
import { User } from '@vimeo/viewer';
import { ViewerContext } from '@vimeo/viewer';

import EmptyLiveHero from '../components/EmptyLiveHero';
import LiveGridItem from '../components/LiveGridItem';
import LiveHero from '../components/LiveHero';
import LiveNav from '../components/LiveNav';
import LivePlayer from '../components/LivePlayer';
import LoadingSpinner from '../components/LoadingSpinner';
import PaginationFooter from '../components/PaginationLayout/PaginationFooter';
import SearchGrid from '../components/Search/SearchGrid';
import { useSearchDispatch, useSearchState } from '../components/SearchContext';
import { EmbedShowcaseContext } from '../context/useEmbedShowcase';
import { AlbumLayouts, AlbumThemes, Clip, ClipDataObject, Pictures, VideoData } from '../types';
import {
  ALBUM_SORTS,
  albumVideosRequestFields,
  DEFAULT_SORT,
  LAYOUT_LIVE,
  VIDEO_KEY,
} from '../utils/constants';
import { getDefaultThumbnailUri, getLayoutPageSize, idFromUri, parseQuery } from '../utils/helpers';

const NEXT_VIDEO = 'next';
const PREVIOUS_VIDEO = 'previous';

const getVideoParam = (location: Location) => parseQuery(location.search).video || null;

type Props = {
  allowShare: boolean;
  curUser?: User;
  customLogo: Pictures;
  name: string;
  openShare: VoidFunction;
  openWatch?: VoidFunction;
  videos: ClipDataObject[];
  albumId: number;
  brandColor: string;
  albumIsEmpty: boolean;
  theme: AlbumThemes;
  allowDownloads: boolean;
  setDownloadClipId?: (id: number) => void;
  openDownloadModal?: () => void;
  continuousPlay?: boolean;
  page: number;
  setPage: (n: number) => void;
  totalVideos: number;
  isUpdating: boolean;
  layout: AlbumLayouts;
  isOffsite?: boolean;
  isOwnerOrAdmin: boolean;
  webCustomLogo: boolean;
  // API returns both types depending on if the custom thumbnail is set
  pictures: Pictures | Pictures[];
  sort: string;
  hideUpcoming: boolean;
  hashedPass?: string;
  albumOwnerId?: number;
  teamRole?: string;
  hasChosenThumbnail: boolean;
};

function Live({
  albumId,
  allowShare,
  brandColor,
  curUser,
  customLogo,
  name,
  openShare,
  openWatch,
  theme,
  videos,
  albumIsEmpty,
  allowDownloads,
  setDownloadClipId,
  openDownloadModal,
  continuousPlay = false,
  page,
  setPage,
  totalVideos,
  isUpdating,
  layout,
  isOffsite = false,
  isOwnerOrAdmin,
  webCustomLogo,
  pictures,
  sort,
  hideUpcoming,
  hashedPass,
  albumOwnerId,
  teamRole,
  hasChosenThumbnail,
}: Props) {
  const history = useHistory();
  const videoIdFromParam = getVideoParam(history.location);
  const hasLinkedClip = videoIdFromParam && videoIdFromParam.length;
  const videoId = hasLinkedClip ? videoIdFromParam : null;
  const { isSearching, isLoading, hasErrored, results, searchPage, searchTotal } = useSearchState();
  const searchDispatch = useSearchDispatch();

  const albumSort = sort && (ALBUM_SORTS as any)[sort] ? (ALBUM_SORTS as any)[sort] : DEFAULT_SORT;

  const viewer = useContext(ViewerContext);
  const embedShowcaseData = useContext(EmbedShowcaseContext);

  const defaultThumbnail = useMemo(
    () => getDefaultThumbnailUri(hasChosenThumbnail ? pictures : []),
    [pictures, hasChosenThumbnail],
  );

  if (!viewer) {
    return null;
  }

  const { apiUrl, vimeoHttpsUrl, jwt } = viewer;
  const [isPlaying, setIsPlaying] = useState(!!hasLinkedClip);
  const [selectedVideoId, setSelectedVideoId] = useState(0);
  const [displayArrows, setDisplayArrows] = useState(videos.length > 1);

  const getVideoIdFromURL = () => {
    const id = getVideoParam(history.location);
    // return if invalid video id
    if (!id || Number(id) < 0) {
      return;
    }

    return Number(id);
  };

  const getRoute = (destination: string, isEmbed?: boolean) => {
    if (isEmbed) {
      return `/showcase/${albumId}/embed2`;
    }

    if (isOffsite) {
      return '' + destination;
    }
    return `/showcase/${albumId}` + destination;
  };

  const onBack = useCallback(() => {
    FA.trackClick({
      container: 'live_event',
      component: 'live_web_layout',
      keyword: 'back',
    });
    if (teamRole) {
      FA.trackClick({
        container: 'team_home_page',
        component: 'live_web_layout',
        keyword: 'back_to_team_home_page_' + teamRole.toLowerCase(),
      });
    }
    setIsPlaying(false);
    history.replace(getRoute('', embedShowcaseData?.isShowcaseEmbeded));
  }, [isPlaying]);

  useEffect(() => {
    // If there's a clip id in the initial url then query for the clip data.
    if (hasLinkedClip) {
      linkedClipQuery();
    }
    history.listen(() => {
      const id = getVideoIdFromURL();

      if (id) {
        setIsPlaying(true);
        setSelectedVideoId(id);
      } else {
        setIsPlaying(false);
      }
    });
  }, [hasLinkedClip]);

  /** Return the index position of the video in the video list from videoId
   *  Return -1 if not found
   */
  const getVideoPositionFromId = (id: number) => {
    return videos.findIndex((video) => {
      if (!video.uri) {
        return -1;
      }
      const videoInListId = parseInt(video.uri.split('videos/')[1], 10);
      return id === videoInListId;
    });
  };

  const videoQueryVars = {
    ...albumSort,
    fields: albumVideosRequestFields,
    page: 1,
    per_page: 1,
    filter: hideUpcoming ? 'playable' : '',
    _hashed_pass: hashedPass || '',
    is_embed: embedShowcaseData?.isShowcaseEmbeded,
    referrer: embedShowcaseData?.referrer,
  };

  const { data: featuredVideoData } = useQuery<VideoData>(`/albums/${albumId}/videos`, {
    variables: videoQueryVars,
    accept: [{ resource: 'video', version: '3.4.9' }],
  });
  const featuredVideo =
    featuredVideoData && featuredVideoData.items.length ? featuredVideoData.items[0] : videos[0];

  const [linkedClipQuery, { data: linkedClip, error: linkedClipError }] = useLazyQuery<Clip>(
    `/albums/${albumId}/videos/${videoId}`,
    {
      variables: videoQueryVars,
    },
  );

  // if its an invalid clip, redirect to showcase page
  useEffect(() => {
    if (linkedClip && !linkedClip.uri && videoIdFromParam) {
      history.replace(getRoute(''));
    }
  }, [linkedClip]);

  const selectedVideoIndex = getVideoPositionFromId(selectedVideoId);
  const selectedVideo = useMemo(() => {
    const idFromParam = getVideoParam(history.location);
    if (linkedClip && linkedClip.uri && idFromParam) {
      const linkedClipId = linkedClip.uri.split('videos/')[1];
      if (idFromParam === linkedClipId) {
        return linkedClip;
      }
    }

    // if its the featured video, and it's not in the video list
    if (selectedVideoIndex < 0 && featuredVideo && featuredVideo.uri) {
      const featuredVideoId = parseInt(featuredVideo.uri.split('videos/')[1], 10);
      if (selectedVideoId === featuredVideoId) {
        return featuredVideo;
      }
    }

    return videos[selectedVideoIndex];
  }, [selectedVideoIndex, featuredVideo, linkedClip]);

  if (linkedClipError) {
    throw linkedClipError;
  }

  // Wait for a video query if its expected.
  if ((hasLinkedClip && !selectedVideo) || (!featuredVideo && !albumIsEmpty)) {
    return null;
  }

  const onPlay = (id: number) => {
    if (isSearching) {
      searchDispatch({ type: 'CLOSE_SEARCH' });
    }
    window.scrollTo(0, 0);
    history.push(getRoute(`?video=${id}`));
  };

  const cycleVideos = (direction: string) => {
    const totalCurrentVideos = videos.length;

    if (selectedVideoIndex >= 0) {
      const lastVideoIndex = totalCurrentVideos - 1;

      let nextVideoIndex = selectedVideoIndex === lastVideoIndex ? 0 : selectedVideoIndex + 1;
      let previousVideoIndex = selectedVideoIndex === 0 ? lastVideoIndex : selectedVideoIndex - 1;

      if (page > 1) {
        nextVideoIndex = selectedVideoIndex === lastVideoIndex ? -1 : selectedVideoIndex + 1;
        previousVideoIndex = selectedVideoIndex === 0 ? -1 : selectedVideoIndex - 1;
      }

      const index = direction === NEXT_VIDEO ? nextVideoIndex : previousVideoIndex;

      if (teamRole) {
        FA.trackClick({
          container: 'team_home_page',
          component: 'live_web_layout',
          keyword: direction === PREVIOUS_VIDEO ? 'clicked_left' : 'clicked_right',
        });
      }

      if (index >= 0) {
        const newVideoId = parseInt(videos[index].uri.split('videos/')[1], 10);
        onPlay(newVideoId);
      }

      if (index < 0 && featuredVideo) {
        const newVideoId = parseInt(featuredVideo.uri.split('videos/')[1], 10);
        onPlay(newVideoId);
      }
    } else {
      // when video id match not found
      // play first video in grid list if clicked next
      // play last video in grid list if clicked prev
      const index = direction === NEXT_VIDEO ? 0 : totalVideos - 1;
      const newVideoId = parseInt(videos[index].uri.split('videos/')[1], 10);
      onPlay(newVideoId);
    }
  };

  if (albumIsEmpty) {
    return (
      <div style={{ position: 'relative' }}>
        <LiveNav
          allowShare={allowShare}
          avatar={curUser && curUser.pictures}
          userName={curUser && curUser.name}
          logo={customLogo}
          name={name}
          openShare={openShare}
          webCustomLogo={webCustomLogo}
          albumOwnerId={albumOwnerId}
          teamRole={teamRole}
        />
        <EmptyLiveHero
          isOwnerOrAdmin={isOwnerOrAdmin}
          albumId={albumId}
          defaultThumbnail={defaultThumbnail}
        />
      </div>
    );
  }

  return (
    <>
      {isPlaying && selectedVideo && selectedVideo.displayType !== 'CLIP_PLACEHOLDER' ? (
        <LivePlayer
          onBack={onBack}
          brandColor={brandColor}
          theme={theme}
          albumId={albumId}
          selectedVideo={selectedVideo}
          onNext={() => cycleVideos(NEXT_VIDEO)}
          onPrevious={() => cycleVideos(PREVIOUS_VIDEO)}
          continuousPlay={continuousPlay}
          displayArrows={displayArrows}
          baseUrl={vimeoHttpsUrl}
          jwt={jwt}
          hashedPass={hashedPass}
        />
      ) : (
        <LiveWrapper theme={theme}>
          {featuredVideo && (
            <div>
              <LiveNav
                allowShare={allowShare}
                avatar={curUser && curUser.pictures}
                userName={curUser && curUser.name}
                logo={customLogo}
                name={name}
                openShare={openShare}
                webCustomLogo={webCustomLogo}
                albumOwnerId={albumOwnerId}
                teamRole={teamRole}
              />
              {!isSearching && (
                <LiveHero
                  albumId={albumId}
                  brandColor={brandColor}
                  defaultThumbnail={defaultThumbnail}
                  logo={!!customLogo}
                  name={name}
                  onPlay={() => {
                    if (openWatch) {
                      openWatch();
                    }
                    if (!featuredVideo || !featuredVideo.uri) {
                      return;
                    }
                    if (!displayArrows) {
                      setDisplayArrows(true);
                    }
                    const id = parseInt(featuredVideo.uri.split('videos/')[1], 10);
                    onPlay(id);
                  }}
                  featuredVideo={featuredVideo}
                  apiUrl={apiUrl}
                  apiJwt={jwt}
                  hashedPass={hashedPass}
                />
              )}
            </div>
          )}
          {isUpdating ? (
            <LoadingSpinner />
          ) : (
            <BottomSwatch>
              <>
                {isSearching ? (
                  <SearchGrid
                    results={results}
                    page={searchPage}
                    totalVideos={searchTotal}
                    isLoading={isLoading}
                    hasErrored={hasErrored}
                    onPlay={onPlay}
                  />
                ) : (
                  <VideoGrid>
                    {videos.map((video, index) => {
                      if (page === 1 && index === 0) {
                        return null;
                      }
                      return (
                        <LiveGridItem
                          key={
                            video.displayType === 'CLIP_PLACEHOLDER'
                              ? index
                              : idFromUri(video?.uri, VIDEO_KEY)
                          }
                          brandColor={brandColor}
                          title={video.name}
                          description={video.description}
                          duration={video.duration}
                          thumbnails={video.pictures}
                          onPlay={() => {
                            if (!displayArrows) {
                              setDisplayArrows(true);
                            }
                            if (video.displayType !== 'CLIP_PLACEHOLDER') {
                              const id = parseInt(video.uri.split('videos/')[1], 10);
                              onPlay(id);

                              FA.trackClick({
                                container: 'showcase',
                                component: 'live_video_row',
                                keyword: index,
                              });

                              if (teamRole) {
                                FA.trackClick({
                                  container: 'team_home_page',
                                  component: 'live_video_row',
                                  keyword: index,
                                });
                              }
                            }
                          }}
                          type={video.type}
                          live={video.live}
                          theme={theme}
                          allowDownloads={allowDownloads && video.privacy.download}
                          setDownloadClipId={setDownloadClipId}
                          openDownloadModal={openDownloadModal}
                          displayType={video.displayType}
                          videoId={
                            video.displayType === 'CLIP_PLACEHOLDER'
                              ? index
                              : idFromUri(video?.uri, VIDEO_KEY)
                          }
                        />
                      );
                    })}
                  </VideoGrid>
                )}
                {!isSearching && (
                  <PaginationWrapper>
                    <PaginationFooter
                      theme={theme}
                      currentPage={page}
                      pageSize={getLayoutPageSize(LAYOUT_LIVE)}
                      totalItems={totalVideos}
                      onSetPage={setPage}
                      layout={layout}
                    />
                  </PaginationWrapper>
                )}
              </>
            </BottomSwatch>
          )}
        </LiveWrapper>
      )}
    </>
  );
}

const LiveWrapper = styled.div<{ theme: AlbumThemes }>`
  width: 100%;
  background-color: ${(props) => (props.theme === 'dark' ? 'black' : 'white')};
  color: ${(props) => (props.theme === 'dark' ? 'white' : 'black')};
  position: relative;
`;

const VideoGrid = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  display: grid;
  margin: 0 auto;
  padding: ${rem(25)};
  grid-template-columns: repeat(auto-fill, minmax(${rem('280px')}, 1fr));
  min-width: ${rem(330)};
  max-width: ${rem(1600)};
  @media only screen and (max-width: 767px) {
    padding: ${rem(10)};
  }
`;

const BottomSwatch = styled.div`
  padding-bottom: ${rem(60)};
`;

const PaginationWrapper = styled.div`
  padding: 0 ${rem(25)};
  max-width: ${rem(1600)};
  margin: 0 auto;
`;

export default Live;
